import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, Loader2, Database, CheckCircle2, AlertTriangle, Download, Copy, ShoppingBag, Link as LinkIcon, RefreshCw, Plug, Clock, ShoppingCart, BookOpen, Unplug, KeyRound, Plus, Trash2, Pencil, Check, X, GripVertical, Star, Globe, FileText } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-2">
      <span className="text-[11px] tracking-[0.12em] uppercase text-[var(--text-muted)] font-semibold">{label}</span>
      {children}
      {hint && <span className="block text-xs text-[var(--text-muted)]">{hint}</span>}
    </label>
  );
}

const input = "w-full bg-[var(--bg-card)] border border-[var(--border-color)] px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all";

// ── Platform Cards Data ──────────────────────────────────
const ACCOUNTING_PLATFORMS = [
  {
    id: "qoyod", name: "قيود", name_en: "Qoyod",
    logo: "ق", color: "#004EEB", bg: "bg-[#004EEB]/10",
    type: "active", desc: "محاسبة سعودية",
    logoUrl: "https://www.google.com/s2/favicons?domain=qoyod.com&sz=128",
  },
  {
    id: "wafeq", name: "وافق", name_en: "Wafeq",
    logo: "و", color: "#1565C0", bg: "bg-blue-50",
    type: "soon", desc: "محاسبة إماراتية",
    logoUrl: "https://www.google.com/s2/favicons?domain=wafeq.com&sz=128",
  },
  {
    id: "daftra", name: "دفترة", name_en: "Daftra",
    logo: "د", color: "#F4651C", bg: "bg-orange-50",
    type: "soon", desc: "محاسبة مصرية/عربية",
    logoUrl: "https://www.google.com/s2/favicons?domain=daftra.com&sz=128",
  },
  {
    id: "odoo", name: "أودو", name_en: "Odoo",
    logo: "O", color: "#714B67", bg: "bg-purple-50",
    type: "soon", desc: "ERP شامل",
    logoUrl: "https://cdn.simpleicons.org/odoo/714B67",
  },
  {
    id: "zoho", name: "Zoho Books", name_en: "Zoho Books",
    logo: "Z", color: "#E42527", bg: "bg-red-50",
    type: "soon", desc: "محاسبة سحابية",
    logoUrl: "https://cdn.simpleicons.org/zoho/E42527",
  },
  {
    id: "qb", name: "كويك بوكس", name_en: "QuickBooks",
    logo: "Q", color: "#2CA01C", bg: "bg-green-50",
    type: "soon", desc: "المحاسبة العالمية",
    logoUrl: "https://www.google.com/s2/favicons?domain=quickbooks.intuit.com&sz=128",
  },
  {
    id: "sap", name: "SAP B1", name_en: "SAP Business One",
    logo: "S", color: "#0070F2", bg: "bg-sky-50",
    type: "soon", desc: "للمؤسسات الكبرى",
    logoUrl: "https://cdn.simpleicons.org/sap/0070F2",
  },
  {
    id: "xero", name: "Xero", name_en: "Xero",
    logo: "X", color: "#13B5EA", bg: "bg-cyan-50",
    type: "soon", desc: "محاسبة سحابية عالمية",
    logoUrl: "https://cdn.simpleicons.org/xero/13B5EA",
  },
];

const ECOMMERCE_PLATFORMS = [
  {
    id: "salla", name: "سلة", name_en: "Salla",
    logo: "س", color: "#5B2BE7", bg: "bg-violet-50",
    type: "active", desc: "متجر إلكتروني سعودي",
    logoUrl: "https://www.google.com/s2/favicons?domain=salla.sa&sz=128",
  },
  {
    id: "zid", name: "زد", name_en: "Zid",
    logo: "ز", color: "#1B1F3A", bg: "bg-slate-100",
    type: "soon", desc: "منصة سعودية",
    logoUrl: "https://www.google.com/s2/favicons?domain=zid.sa&sz=128",
  },
  {
    id: "shopify", name: "شوبيفاي", name_en: "Shopify",
    logo: "S", color: "#96BF48", bg: "bg-lime-50",
    type: "soon", desc: "متجر عالمي",
    logoUrl: "https://cdn.simpleicons.org/shopify/96BF48",
  },
  {
    id: "woo", name: "WooCommerce", name_en: "WooCommerce",
    logo: "W", color: "#7F54B3", bg: "bg-purple-50",
    type: "soon", desc: "على WordPress",
    logoUrl: "https://cdn.simpleicons.org/woocommerce/7F54B3",
  },
  {
    id: "expandcart", name: "اكسباندكارت", name_en: "ExpandCart",
    logo: "E", color: "#E67E22", bg: "bg-amber-50",
    type: "soon", desc: "منصة عربية",
    logoUrl: "https://www.google.com/s2/favicons?domain=expandcart.com&sz=128",
  },
  {
    id: "opencart", name: "OpenCart", name_en: "OpenCart",
    logo: "O", color: "#23AACF", bg: "bg-cyan-50",
    type: "soon", desc: "مفتوح المصدر",
    logoUrl: "https://cdn.simpleicons.org/woocommerce/96588A",
  },
];

function PlatformCard({ id, name, name_en, logo, color, bg, type, desc, connected, logoUrl }) {
  const [imgError, setImgError] = useState(false);
  const isConnected = connected;
  const isActive = type === "active";
  return (
    <div
      className={`relative flex flex-col p-4 rounded-2xl border transition-all group
        ${isConnected ? "border-emerald-200 bg-emerald-50/40 shadow-sm"
          : isActive ? "border-[var(--border-color)] bg-[var(--bg-card)] shadow-sm hover:border-slate-300 hover:shadow-md cursor-pointer"
          : "border-[var(--border-color)] bg-[var(--bg-card-hover)]/40 opacity-60"}`}
    >
      {isConnected && (
        <span className="absolute top-2 end-2 flex items-center gap-1 text-[9px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">
          <CheckCircle2 className="w-2.5 h-2.5" /> متصل
        </span>
      )}
      {!isConnected && type === "soon" && (
        <span className="absolute top-2 end-2 text-[9px] font-bold text-[var(--text-muted)] bg-slate-100 px-1.5 py-0.5 rounded-full">
          قريباً
        </span>
      )}

      <div className={`w-12 h-12 rounded-xl grid place-items-center mb-3 ${bg} ring-1 ring-black/5 transition-transform group-hover:scale-105 overflow-hidden`}>
        {logoUrl && !imgError ? (
          <img
            src={logoUrl}
            alt={name_en}
            className="w-8 h-8 object-contain"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="text-xl font-black" style={{ color }}>{logo}</span>
        )}
      </div>

      <div className="font-bold text-[var(--text-primary)] text-sm leading-tight">{name}</div>
      <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{name_en}</div>
      <div className="text-[10px] text-[var(--text-secondary)] mt-1.5 leading-tight">{desc}</div>
    </div>
  );
}

/* ══════════════ Help Topics Manager ══════════════ */
function HelpTopicsSection({ isAdmin = false }) {
  const [topics, setTopics]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd]   = useState(false);

  const emptyForm = { title: "", content: "", order: 0, active: true };
  const [form, setForm]         = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      // admins see all (incl inactive), users see only active
      const endpoint = isAdmin ? "/help/topics/all" : "/help/topics";
      const { data } = await api.get(endpoint);
      setTopics(data.topics || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [isAdmin]);

  const addTopic = async () => {
    if (!form.title.trim()) { toast.error("العنوان مطلوب"); return; }
    setSaving(true);
    try {
      await api.post("/help/topics", form);
      toast.success("تم إضافة الموضوع");
      setForm(emptyForm);
      setShowAdd(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "خطأ"); }
    setSaving(false);
  };

  const updateTopic = async (id) => {
    if (!editForm.title.trim()) { toast.error("العنوان مطلوب"); return; }
    setSaving(true);
    try {
      await api.put(`/help/topics/${id}`, editForm);
      toast.success("تم التحديث");
      setEditingId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "خطأ"); }
    setSaving(false);
  };

  const deleteTopic = async (id) => {
    if (!window.confirm("هل تريد حذف هذا الموضوع؟")) return;
    try {
      await api.delete(`/help/topics/${id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "خطأ"); }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/help/topics/${t.id}`, { active: !t.active });
      load();
    } catch {}
  };

  return (
    <section className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-3xl p-6 shadow-sm fade-up">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#004EEB]" />
          <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-bold">مواضيع المساعدة</span>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setShowAdd(!showAdd); setForm(emptyForm); }}
            data-testid="add-help-topic-btn"
            className="flex items-center gap-1.5 text-xs font-bold text-[#004EEB] bg-[#004EEB]/10 hover:bg-[#004EEB]/20 px-3 py-1.5 rounded-xl transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            إضافة موضوع
          </button>
        )}
      </div>

      <h2 className="text-lg font-black text-[var(--text-primary)] tracking-tight mb-1">مواضيع المساعدة</h2>
      <p className="text-sm text-[var(--text-secondary)] mb-5">
        {isAdmin ? "المواضيع التي ستظهر للمستخدمين في قسم المساعدة بالشريط العلوي." : "مواضيع للمساعدة والإجابة على الأسئلة الشائعة."}
      </p>

      {/* Add form — admin only */}
      {isAdmin && showAdd && (
        <div className="bg-[var(--bg-card-hover)] border border-[var(--border-color)] rounded-2xl p-4 mb-4 space-y-3">
          <p className="text-xs font-bold text-[var(--text-secondary)] mb-2">موضوع جديد</p>
          <div>
            <label className="text-xs font-semibold text-[var(--text-secondary)] mb-1 block">العنوان *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="مثال: كيف أربط متجر سلة؟"
              data-testid="help-topic-title-input"
              className="w-full border border-[var(--border-color)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[var(--text-secondary)] mb-1 block">المحتوى *</label>
            <textarea
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="اكتب شرح الموضوع هنا..."
              data-testid="help-topic-content-input"
              rows={4}
              className="w-full border border-[var(--border-color)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none"
            />
          </div>
          <div className="flex items-center gap-3">
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)] mb-1 block">الترتيب</label>
              <input
                type="number"
                min={0}
                value={form.order}
                onChange={e => setForm(f => ({ ...f, order: +e.target.value }))}
                className="w-20 border border-[var(--border-color)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                id="add-active"
                checked={form.active}
                onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                className="accent-blue-600 w-4 h-4"
              />
              <label htmlFor="add-active" className="text-xs text-[var(--text-secondary)]">مفعّل</label>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={addTopic}
              disabled={saving}
              data-testid="save-help-topic-btn"
              className="flex items-center gap-1.5 bg-[#004EEB] text-white text-xs font-bold px-4 py-2 rounded-xl hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              حفظ
            </button>
            <button onClick={() => setShowAdd(false)}
              className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] px-3 py-2 rounded-xl hover:bg-slate-100">
              إلغاء
            </button>
          </div>
        </div>
      )}

      {/* Topics list */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : topics.length === 0 ? (
        <div className="text-center py-10 text-[var(--text-muted)] border-2 border-dashed border-[var(--border-color)] rounded-2xl">
          <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">{isAdmin ? "لا توجد مواضيع بعد — أضف موضوعاً الآن" : "لا توجد مواضيع مساعدة متاحة حالياً."}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {topics.map((t) => (
            <div
              key={t.id}
              data-testid={`help-topic-${t.id}`}
              className={`border rounded-2xl overflow-hidden transition-all ${
                t.active ? "border-[var(--border-color)] bg-[var(--bg-card)]" : "border-[var(--border-color)] bg-[var(--bg-card-hover)] opacity-60"
              }`}
            >
              {isAdmin && editingId === t.id ? (
                /* Edit mode */
                <div className="p-4 space-y-3">
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                    data-testid={`edit-topic-title-${t.id}`}
                    className="w-full border border-[var(--border-color)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <textarea
                    value={editForm.content}
                    onChange={e => setEditForm(f => ({ ...f, content: e.target.value }))}
                    data-testid={`edit-topic-content-${t.id}`}
                    rows={4}
                    className="w-full border border-[var(--border-color)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none"
                  />
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={0}
                      value={editForm.order}
                      onChange={e => setEditForm(f => ({ ...f, order: +e.target.value }))}
                      className="w-20 border border-[var(--border-color)] rounded-xl px-3 py-2 text-sm focus:outline-none"
                    />
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id={`edit-active-${t.id}`}
                        checked={editForm.active}
                        onChange={e => setEditForm(f => ({ ...f, active: e.target.checked }))}
                        className="accent-blue-600 w-4 h-4" />
                      <label htmlFor={`edit-active-${t.id}`} className="text-xs text-[var(--text-secondary)]">مفعّل</label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => updateTopic(t.id)} disabled={saving}
                      className="flex items-center gap-1 bg-[#004EEB] text-white text-xs font-bold px-3 py-1.5 rounded-xl hover:bg-blue-700 disabled:opacity-50">
                      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} حفظ
                    </button>
                    <button onClick={() => setEditingId(null)}
                      className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] px-3 py-1.5 rounded-xl hover:bg-slate-100">
                      إلغاء
                    </button>
                  </div>
                </div>
              ) : (
                /* View mode */
                <div className="flex items-start gap-3 p-4">
                  <GripVertical className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-bold text-[var(--text-primary)]">{t.title}</p>
                      {isAdmin && !t.active && (
                        <span className="text-[10px] font-bold bg-slate-100 text-[var(--text-secondary)] px-2 py-0.5 rounded-full">مخفي</span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] line-clamp-2">{t.content}</p>
                  </div>
                  {/* Admin action buttons */}
                  {isAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => toggleActive(t)}
                        title={t.active ? "إخفاء" : "إظهار"}
                        className={`p-1.5 rounded-lg transition-colors text-xs ${
                          t.active
                            ? "text-green-500 hover:bg-green-50"
                            : "text-[var(--text-muted)] hover:bg-slate-100"
                        }`}
                      >
                        {t.active ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => { setEditingId(t.id); setEditForm({ title: t.title, content: t.content, order: t.order ?? 0, active: t.active }); }}
                        data-testid={`edit-topic-btn-${t.id}`}
                        className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-slate-100 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteTopic(t.id)}
                        data-testid={`delete-topic-btn-${t.id}`}
                        className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


/* ══════════════════════════════════════════════════════
   TESTIMONIALS SECTION — Admin Only
══════════════════════════════════════════════════════ */
const DEFAULT_TESTIMONIALS_SETTINGS = [
  { name: "فهد العتيبي", role: "صاحب متجر ستايل هاوس", city: "الرياض", initials: "فع", color: "#3B82F6", rating: 5, text: "كنا نضيع 3 ساعات يومياً في تحديث المخزون يدوياً بين قيود وسلة. بعد جسر، يحدث كل شيء تلقائياً وبدون أخطاء." },
  { name: "سارة الشمري", role: "مديرة متجر لمسة للعطور", city: "جدة",    initials: "سش", color: "#10B981", rating: 5, text: "الإعداد استغرق أقل من 10 دقائق، والتزامن يعمل بشكل رائع. الدعم الفني متجاوب جداً وسريع." },
  { name: "محمد الغامدي", role: "صاحب متجر تك بلس",     city: "الدمام",  initials: "مغ", color: "#A78BFA", rating: 5, text: "وفّرنا ما يقارب 80% من وقت إدارة المخزون. لا نقلق أبداً من التضارب بين المنصتين." },
];

const AVATAR_COLORS = ["#3B82F6","#10B981","#A78BFA","#F59E0B","#EF4444","#EC4899","#14B8A6","#F97316"];

function getInitials(name) {
  if (!name?.trim()) return "؟";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return parts[0][0] + parts[1][0];
  return parts[0].slice(0, 2);
}

function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/testimonials")
      .then(r => setTestimonials(r.data?.testimonials?.length ? r.data.testimonials : DEFAULT_TESTIMONIALS_SETTINGS))
      .catch(() => setTestimonials(DEFAULT_TESTIMONIALS_SETTINGS));
  }, []);

  const update = (i, key, val) => setTestimonials(prev => prev.map((t, ti) => ti === i ? { ...t, [key]: val, ...(key === "name" ? { initials: getInitials(val) } : {}) } : t));

  const add = () => setTestimonials(prev => [
    ...prev,
    { name: "", role: "", city: "", initials: "؟", color: AVATAR_COLORS[prev.length % AVATAR_COLORS.length], rating: 5, text: "" },
  ]);

  const remove = (i) => { if (testimonials.length <= 1) return; setTestimonials(prev => prev.filter((_, ti) => ti !== i)); };

  const save = async () => {
    const incomplete = testimonials.some(t => !t.name?.trim() || !t.text?.trim());
    if (incomplete) { toast.error("يرجى تعبئة الاسم والنص لكل شهادة"); return; }
    setSaving(true);
    try {
      await api.put("/admin/testimonials", { testimonials });
      toast.success("تم حفظ آراء التجار بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!testimonials) return (
    <section className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-3xl p-6 shadow-sm">
      <div className="flex items-center gap-2 text-[var(--text-muted)]"><Loader2 className="w-4 h-4 animate-spin"/> جاري التحميل...</div>
    </section>
  );

  return (
    <section className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-3xl p-6 shadow-sm fade-up" data-testid="testimonials-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-4 h-4 text-amber-400"/>
            <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-bold">آراء التجار</span>
          </div>
          <h2 className="text-lg font-black text-[var(--text-primary)] tracking-tight">إدارة شهادات التجار</h2>
          <p className="text-xs text-[var(--text-secondary)] mt-1">تظهر في الصفحة العامة. أضف آراء حقيقية من تجارك.</p>
        </div>
        <button type="button" onClick={add}
          className="flex items-center gap-1.5 text-xs font-bold text-[#004EEB] hover:bg-[#004EEB]/8 px-3 py-2 rounded-xl transition-colors">
          <Plus className="w-3.5 h-3.5"/> إضافة رأي
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
        {testimonials.map((t, i) => (
          <div key={i} data-testid={`testimonial-edit-${i}`}
            className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card-hover)] p-5 relative group">

            {/* Delete button */}
            <button type="button" onClick={() => remove(i)}
              className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg flex items-center justify-center text-[var(--text-muted)] hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all">
              <Trash2 className="w-3.5 h-3.5"/>
            </button>

            {/* Avatar preview */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 transition-transform hover:scale-105 cursor-pointer"
                style={{ background: `linear-gradient(135deg, ${t.color}, ${t.color}cc)` }}>
                {t.initials || getInitials(t.name)}
              </div>
              <div className="flex-1 min-w-0">
                <input className={input} value={t.name} onChange={e => update(i, "name", e.target.value)} placeholder="اسم التاجر"/>
              </div>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">المنصب</label>
                  <input className={input} value={t.role} onChange={e => update(i, "role", e.target.value)} placeholder="صاحب متجر..."/>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">المدينة</label>
                  <input className={input} value={t.city} onChange={e => update(i, "city", e.target.value)} placeholder="الرياض"/>
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-2 block">التقييم</label>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(s => (
                    <button key={s} type="button" onClick={() => update(i, "rating", s)}>
                      <Star className={`w-5 h-5 transition-colors ${s <= (t.rating||5) ? 'fill-amber-400 text-amber-400' : 'text-[var(--border-color)]'}`} strokeWidth={1.5}/>
                    </button>
                  ))}
                  <span className="text-xs text-[var(--text-muted)] mr-2">{t.rating || 5}/5</span>
                </div>
              </div>

              {/* Color */}
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-2 block">لون الأفاتار</label>
                <div className="flex gap-1.5 flex-wrap">
                  {AVATAR_COLORS.map(c => (
                    <button key={c} type="button" onClick={() => update(i, "color", c)}
                      className="w-6 h-6 rounded-full transition-transform hover:scale-110 ring-offset-1"
                      style={{ background: c, outline: t.color === c ? `2px solid ${c}` : 'none', outlineOffset: '2px' }}/>
                  ))}
                </div>
              </div>

              {/* Text */}
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">نص الشهادة</label>
                <textarea
                  className="w-full bg-[var(--bg-card)] border border-[var(--border-color)] px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all resize-none text-[var(--text-primary)]"
                  rows={3} value={t.text} onChange={e => update(i, "text", e.target.value)}
                  placeholder="اكتب تجربة التاجر بكلماته..."/>
              </div>
            </div>
          </div>
        ))}

        {/* Add card */}
        <button type="button" onClick={add}
          className="rounded-2xl border-2 border-dashed border-[var(--border-color)] hover:border-[#004EEB]/50 flex flex-col items-center justify-center gap-3 p-8 text-[var(--text-muted)] hover:text-[#004EEB] transition-all group min-h-[220px]"
          data-testid="add-testimonial-btn">
          <div className="w-12 h-12 rounded-full border-2 border-dashed border-current flex items-center justify-center group-hover:scale-110 transition-transform">
            <Plus className="w-5 h-5"/>
          </div>
          <span className="text-sm font-semibold">إضافة شهادة جديدة</span>
        </button>
      </div>

      <div className="flex justify-end mt-6">
        <button type="button" onClick={save} disabled={saving}
          data-testid="save-testimonials"
          className="flex items-center gap-2 bg-[#004EEB] hover:bg-[#003BBB] disabled:opacity-60 text-white px-6 py-2.5 rounded-xl font-semibold text-sm transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
          حفظ الشهادات
        </button>
      </div>
    </section>
  );
}



/* ══════════════════════════════════════════════════════
   BLOG SECTION — Admin Only
══════════════════════════════════════════════════════ */

const BLOG_CATEGORIES = ["نمو المبيعات","دليل تقني","إدارة المخزون","تحسين الأداء","إدارة الأعمال","أخرى"];
const AUTHOR_COLORS   = ["#004EEB","#10B981","#A78BFA","#F59E0B","#EF4444","#EC4899","#14B8A6"];

const EMPTY_FORM = { title:"", slug:"", excerpt:"", content:"", category:"نمو المبيعات", read_time:5, author:"فريق جسر", author_initials:"جس", author_color:"#004EEB", published:true };

function BlogSection() {
  const [posts,    setPosts]    = useState(null);
  const [editing,  setEditing]  = useState(null); // null | "new" | post.id
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [saving,   setSaving]   = useState(false);

  const load = () =>
    api.get("/admin/blog")
      .then(r => setPosts(r.data?.posts || []))
      .catch(() => setPosts([]));

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ ...EMPTY_FORM, slug: `post-${Date.now()}` });
    setEditing("new");
  };

  const openEdit = (post) => {
    setForm({ ...post });
    setEditing(post.id);
  };

  const cancel = () => setEditing(null);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.title?.trim() || !form.excerpt?.trim()) {
      toast.error("العنوان والمقتطف مطلوبان"); return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/blog", form);
        toast.success("تم إضافة المقال");
      } else {
        await api.put(`/admin/blog/${editing}`, form);
        toast.success("تم تحديث المقال");
      }
      await load(); setEditing(null);
    } catch (e) { toast.error(formatApiError(e));
    } finally   { setSaving(false); }
  };

  const deletePost = async (id) => {
    if (!window.confirm("هل تريد حذف هذا المقال نهائياً؟")) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      await load(); toast.success("تم الحذف");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePublished = async (post) => {
    try {
      await api.put(`/admin/blog/${post.id}`, { ...post, published: !post.published });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!posts) return (
    <section className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-3xl p-6 shadow-sm">
      <div className="flex items-center gap-2 text-[var(--text-muted)]"><Loader2 className="w-4 h-4 animate-spin"/> جاري التحميل...</div>
    </section>
  );

  return (
    <section className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-3xl p-6 shadow-sm fade-up" data-testid="blog-section">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-[#004EEB]"/>
            <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-bold">المدونة</span>
          </div>
          <h2 className="text-lg font-black text-[var(--text-primary)] tracking-tight">إدارة مقالات المدونة</h2>
          <p className="text-xs text-[var(--text-secondary)] mt-1">تظهر في الصفحة العامة /blog — يمكن إضافة وتعديل وحذف المقالات.</p>
        </div>
        {!editing && (
          <button type="button" onClick={openNew}
            className="flex items-center gap-1.5 text-xs font-bold text-[#004EEB] hover:bg-[#004EEB]/8 px-3 py-2 rounded-xl transition-colors"
            data-testid="blog-add-btn">
            <Plus className="w-3.5 h-3.5"/> مقال جديد
          </button>
        )}
      </div>

      {/* Edit / New form */}
      {editing && (
        <div className="rounded-2xl border border-[#004EEB]/20 bg-[#004EEB]/5 p-6 mb-6 space-y-4" data-testid="blog-edit-form">
          <h3 className="font-bold text-[var(--text-primary)] text-base">
            {editing === "new" ? "إضافة مقال جديد" : "تعديل المقال"}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">العنوان *</label>
              <input className={input} value={form.title} onChange={e => setF("title", e.target.value)} placeholder="عنوان المقال" data-testid="blog-form-title"/>
            </div>
            <div>
              <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">الرابط (Slug)</label>
              <input className={input} value={form.slug} onChange={e => setF("slug", e.target.value)} placeholder="post-slug" dir="ltr"/>
            </div>
            <div>
              <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">التصنيف</label>
              <select className={input} value={form.category} onChange={e => setF("category", e.target.value)}>
                {BLOG_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">وقت القراءة (دقائق)</label>
              <input className={input} type="number" min={1} max={60} value={form.read_time} onChange={e => setF("read_time", Number(e.target.value))}/>
            </div>
            <div>
              <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">الكاتب</label>
              <input className={input} value={form.author} onChange={e => setF("author", e.target.value)} placeholder="فريق جسر"/>
            </div>
            <div>
              <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">اختصار الاسم</label>
              <input className={input} value={form.author_initials} onChange={e => setF("author_initials", e.target.value)} placeholder="جس" maxLength={2}/>
            </div>
          </div>

          {/* Author color */}
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-2 block">لون الكاتب</label>
            <div className="flex gap-1.5 flex-wrap">
              {AUTHOR_COLORS.map(c => (
                <button key={c} type="button" onClick={() => setF("author_color", c)}
                  className="w-6 h-6 rounded-full transition-transform hover:scale-110"
                  style={{ background: c, outline: form.author_color === c ? `2px solid ${c}` : 'none', outlineOffset: '2px' }}/>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">المقتطف *</label>
            <textarea className="w-full bg-[var(--bg-card)] border border-[var(--border-color)] px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all resize-none text-[var(--text-primary)]"
              rows={2} value={form.excerpt} onChange={e => setF("excerpt", e.target.value)} placeholder="ملخص قصير يظهر في قائمة المقالات" data-testid="blog-form-excerpt"/>
          </div>

          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">المحتوى (HTML)</label>
            <textarea className="w-full bg-[var(--bg-card)] border border-[var(--border-color)] px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all resize-y text-[var(--text-primary)] font-mono"
              rows={8} value={form.content} onChange={e => setF("content", e.target.value)} placeholder="<p>محتوى المقال...</p><h2>عنوان فرعي</h2><p>...</p>" dir="ltr"/>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={e => setF("published", e.target.checked)} className="w-4 h-4 accent-[#004EEB]"/>
              <span className="text-sm font-semibold text-[var(--text-primary)]">منشور (يظهر للجمهور)</span>
            </label>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-[var(--border-color)]">
            <button type="button" onClick={save} disabled={saving} data-testid="blog-form-save"
              className="flex items-center gap-2 bg-[#004EEB] hover:bg-[#003BBB] disabled:opacity-60 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
              {editing === "new" ? "إضافة المقال" : "حفظ التعديلات"}
            </button>
            <button type="button" onClick={cancel}
              className="flex items-center gap-2 border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors">
              <X className="w-4 h-4"/> إلغاء
            </button>
          </div>
        </div>
      )}

      {/* Posts list */}
      {posts.length === 0 ? (
        <div className="text-center py-12 text-[var(--text-muted)]">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-30"/>
          <p className="text-sm">لا توجد مقالات بعد. ابدأ بإضافة مقالك الأول!</p>
        </div>
      ) : (
        <div className="divide-y divide-[var(--border-color)]">
          {posts.map(post => (
            <div key={post.id} data-testid={`blog-post-row-${post.id}`}
              className="flex items-center gap-4 py-4 group">
              {/* Status dot */}
              <div className={`w-2 h-2 rounded-full shrink-0 ${post.published ? 'bg-emerald-400' : 'bg-slate-300'}`}/>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[var(--text-primary)] text-sm line-clamp-1">{post.title}</div>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-xs text-[var(--text-muted)]">{post.category}</span>
                  <span className="text-xs text-[var(--text-muted)]">·</span>
                  <span className="text-xs text-[var(--text-muted)]">{post.read_time} دقائق</span>
                  <span className="text-xs text-[var(--text-muted)]">·</span>
                  <span className={`text-xs font-bold ${post.published ? 'text-emerald-500' : 'text-slate-400'}`}>
                    {post.published ? 'منشور' : 'مسودة'}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => togglePublished(post)} title={post.published ? "إخفاء" : "نشر"}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-[var(--text-muted)] hover:text-[#004EEB] hover:bg-[#004EEB]/8 transition-all">
                  <Globe className="w-4 h-4"/>
                </button>
                <button type="button" onClick={() => openEdit(post)} data-testid={`blog-edit-${post.id}`}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-[var(--text-muted)] hover:text-[#004EEB] hover:bg-[#004EEB]/8 transition-all">
                  <Pencil className="w-4 h-4"/>
                </button>
                <button type="button" onClick={() => deletePost(post.id)} data-testid={`blog-delete-${post.id}`}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-[var(--text-muted)] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all">
                  <Trash2 className="w-4 h-4"/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}



const PLAN_DEFAULTS = [
  { id: "free",       name: "مجاني",    price: "0",   currency: "ريال", period: "دائماً",  desc: "للتجربة والاختبار", features: [{ text: "متجر واحد على سلة", ok: true }, { text: "حتى 100 منتج", ok: true }, { text: "مزامنة يدوية", ok: true }, { text: "دعم المجتمع", ok: true }, { text: "مزامنة تلقائية", ok: false }, { text: "فروع متعددة", ok: false }], cta: "ابدأ مجاناً", highlight: false, badge: "" },
  { id: "pro",        name: "احترافي", price: "99",  currency: "ريال", period: "شهرياً",  desc: "للتجار الجادين",    features: [{ text: "منتجات غير محدودة", ok: true }, { text: "مزامنة تلقائية كل 15 دقيقة", ok: true }, { text: "دعم متعدد الفروع", ok: true }, { text: "استيراد Excel", ok: true }, { text: "تقارير ومخططات متقدمة", ok: true }, { text: "دعم واتساب مباشر", ok: true }], cta: "ابدأ مجاناً 14 يوم", highlight: true, badge: "الأكثر شيوعاً" },
  { id: "enterprise", name: "مؤسسي",   price: "",    currency: "",     period: "",         desc: "للشركات الكبرى",   features: [{ text: "كل مميزات الاحترافي", ok: true }, { text: "فروع غير محدودة", ok: true }, { text: "API مخصص للتكامل", ok: true }, { text: "SLA مضمون 99.9%", ok: true }, { text: "مدير حساب مخصص", ok: true }, { text: "تدريب وتأهيل الفريق", ok: true }], cta: "تحدث مع فريقنا", highlight: false, badge: "" },
];

function PricingPlansSection() {
  const [plans, setPlans] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newFeatures, setNewFeatures] = useState({});

  useEffect(() => {
    api.get("/public/pricing")
      .then(r => setPlans(r.data?.plans?.length ? r.data.plans : PLAN_DEFAULTS))
      .catch(() => setPlans(PLAN_DEFAULTS));
  }, []);

  const updatePlan = (i, key, val) => {
    setPlans(prev => prev.map((p, pi) => pi === i ? { ...p, [key]: val } : p));
  };

  const updateFeature = (pi, fi, key, val) => {
    setPlans(prev => prev.map((p, i) => i !== pi ? p : {
      ...p,
      features: p.features.map((f, j) => j !== fi ? f : { ...f, [key]: val }),
    }));
  };

  const addFeature = (pi) => {
    const text = newFeatures[pi]?.trim();
    if (!text) return;
    setPlans(prev => prev.map((p, i) => i !== pi ? p : {
      ...p, features: [...p.features, { text, ok: true }],
    }));
    setNewFeatures(prev => ({ ...prev, [pi]: "" }));
  };

  const removeFeature = (pi, fi) => {
    setPlans(prev => prev.map((p, i) => i !== pi ? p : {
      ...p, features: p.features.filter((_, j) => j !== fi),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pricing", { plans });
      toast.success("تم حفظ الباقات بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    if (window.confirm("هل تريد إعادة الباقات للإعدادات الافتراضية؟")) {
      setPlans(PLAN_DEFAULTS.map(p => ({ ...p })));
    }
  };

  if (!plans) return (
    <section className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-3xl p-6 shadow-sm">
      <div className="flex items-center gap-2 text-[var(--text-muted)]"><Loader2 className="w-4 h-4 animate-spin"/> جاري التحميل...</div>
    </section>
  );

  return (
    <section className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-3xl p-6 shadow-sm fade-up" data-testid="pricing-plans-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart className="w-4 h-4 text-[#004EEB]"/>
            <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-bold">الباقات</span>
          </div>
          <h2 className="text-lg font-black text-[var(--text-primary)] tracking-tight">إدارة باقات الأسعار</h2>
          <p className="text-xs text-[var(--text-secondary)] mt-1">يظهر مباشرة في الصفحة العامة. التغييرات فورية.</p>
        </div>
        <button onClick={resetDefaults} type="button"
          className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] underline transition-colors">
          إعادة للافتراضي
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {plans.map((plan, pi) => (
          <div key={pi} data-testid={`pricing-edit-plan-${pi}`}
            className={`rounded-2xl p-5 border transition-all ${plan.highlight ? 'border-[#004EEB]/40 bg-[#004EEB]/5' : 'border-[var(--border-color)] bg-[var(--bg-card-hover)]'}`}>

            {/* Plan header */}
            <div className="flex items-center justify-between mb-4">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${plan.highlight ? 'bg-[#004EEB]/15 text-[#004EEB]' : 'bg-[var(--bg-card)] text-[var(--text-muted)]'}`}>
                {pi === 0 ? "الخطة الأولى" : pi === 1 ? "الخطة الوسطى" : "الخطة العليا"}
              </span>
              <label className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] cursor-pointer">
                <input type="checkbox" checked={!!plan.highlight} onChange={e => updatePlan(pi, "highlight", e.target.checked)}
                  className="w-3.5 h-3.5 accent-[#004EEB]"/>
                مميّزة
              </label>
            </div>

            {/* Editable fields */}
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">اسم الباقة</label>
                <input className={input} value={plan.name} onChange={e => updatePlan(pi, "name", e.target.value)} placeholder="مثال: احترافي"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">السعر</label>
                  <input className={input} value={plan.price ?? ""} onChange={e => updatePlan(pi, "price", e.target.value)} placeholder="99 أو فراغ"/>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">العملة/الفترة</label>
                  <input className={input} value={plan.period ?? ""} onChange={e => updatePlan(pi, "period", e.target.value)} placeholder="شهرياً"/>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">الوصف</label>
                <input className={input} value={plan.desc} onChange={e => updatePlan(pi, "desc", e.target.value)} placeholder="للتجار الجادين"/>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">نص الزر</label>
                <input className={input} value={plan.cta} onChange={e => updatePlan(pi, "cta", e.target.value)} placeholder="ابدأ مجاناً"/>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1 block">الشارة (اختياري)</label>
                <input className={input} value={plan.badge ?? ""} onChange={e => updatePlan(pi, "badge", e.target.value)} placeholder="الأكثر شيوعاً"/>
              </div>

              {/* Features list */}
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-2 block">المميزات</label>
                <div className="space-y-1.5 mb-2">
                  {plan.features.map((f, fi) => (
                    <div key={fi} className="flex items-center gap-2">
                      <button type="button" onClick={() => updateFeature(pi, fi, "ok", !f.ok)}
                        className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-colors ${f.ok ? 'bg-emerald-500 border-emerald-500' : 'border-[var(--border-color)] bg-[var(--bg-card)]'}`}>
                        {f.ok && <Check className="w-3 h-3 text-white" strokeWidth={2.5}/>}
                      </button>
                      <input className="flex-1 bg-transparent border-b border-[var(--border-color)] text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#004EEB] pb-0.5 transition-colors"
                        value={f.text} onChange={e => updateFeature(pi, fi, "text", e.target.value)}/>
                      <button type="button" onClick={() => removeFeature(pi, fi)}
                        className="text-[var(--text-muted)] hover:text-red-400 transition-colors shrink-0">
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    className="flex-1 bg-[var(--bg-card)] border border-dashed border-[var(--border-color)] px-3 py-1.5 rounded-lg text-xs focus:outline-none focus:border-[#004EEB] transition-colors text-[var(--text-primary)]"
                    placeholder="أضف ميزة جديدة..."
                    value={newFeatures[pi] ?? ""}
                    onChange={e => setNewFeatures(prev => ({ ...prev, [pi]: e.target.value }))}
                    onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addFeature(pi))}
                  />
                  <button type="button" onClick={() => addFeature(pi)}
                    className="px-3 py-1.5 rounded-lg bg-[#004EEB]/10 text-[#004EEB] text-xs font-bold hover:bg-[#004EEB]/20 transition-colors">
                    <Plus className="w-3.5 h-3.5"/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-6">
        <button type="button" onClick={save} disabled={saving}
          data-testid="save-pricing-plans"
          className="flex items-center gap-2 bg-[#004EEB] hover:bg-[#003BBB] disabled:opacity-60 text-white px-6 py-2.5 rounded-xl font-semibold text-sm transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
          حفظ الباقات
        </button>
      </div>
    </section>
  );
}


export default function SettingsPage() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  const [sallaStatus, setSallaStatus] = useState(null);
  const [sallaTesting, setSallaTesting] = useState(false);
  const [sallaRefreshing, setSallaRefreshing] = useState(false);
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setForm(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    loadStatus();
    loadSallaStatus();
    const id = setInterval(() => { loadStatus(); loadSallaStatus(); }, 3000);

    // Handle OAuth callback result
    const params = new URLSearchParams(location.search);
    const sallaParam = params.get("salla");
    if (sallaParam === "connected") {
      toast.success("تم ربط سلة بنجاح! يمكنك الآن استخدام المزامنة.");
      window.history.replaceState({}, "", "/settings");
    } else if (sallaParam === "error") {
      toast.error("فشل ربط سلة — حاول مجدداً أو تواصل مع الدعم.");
      window.history.replaceState({}, "", "/settings");
    }

    return () => clearInterval(id);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/qoyod/import/status");
      setStatus(data);
    } catch {}
  };

  const loadSallaStatus = async () => {
    try {
      const { data } = await api.get("/salla/status");
      setSallaStatus(data);
    } catch {}
  };

  const [sallaConnecting, setSallaConnecting] = useState(false);
  const [sallaDisconnecting, setSallaDisconnecting] = useState(false);

  const connectSalla = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("auth_token") || "";
    if (!token) { toast.error("يجب تسجيل الدخول أولاً"); return; }

    const url = `${API_URL}/api/salla/connect?token=${encodeURIComponent(token)}`;
    const popup = window.open(url, "salla_connect", "width=520,height=660,scrollbars=yes");

    if (!popup) {
      // Popup blocked — fallback to same-window redirect
      window.location.href = url;
      return;
    }

    setSallaConnecting(true);
    const poll = setInterval(async () => {
      try {
        const { data } = await api.get("/salla/status");
        if (data?.connected) {
          clearInterval(poll);
          setSallaConnecting(false);
          setSallaStatus(data);
          popup.close();
          toast.success("تم ربط سلة بنجاح!");
        }
      } catch {}
      if (popup.closed) {
        clearInterval(poll);
        setSallaConnecting(false);
        loadSallaStatus();
      }
    }, 2000);

    // Auto-stop after 3 minutes
    setTimeout(() => { clearInterval(poll); setSallaConnecting(false); }, 180000);
  };

  if (!form) {
    return <div className="p-10 text-[var(--text-muted)]"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        qoyod_api_key: form.qoyod_api_key || "",
        salla_client_id: form.salla_client_id || "",
        salla_client_secret: form.salla_client_secret || "",
        salla_access_token: form.salla_access_token || "",
        salla_refresh_token: form.salla_refresh_token || "",
        salla_store_id: form.salla_store_id || "",
        salla_store_name: form.salla_store_name || "",
        cron_enabled: !!form.cron_enabled,
        cron_interval_minutes: Number(form.cron_interval_minutes) || 15,
        low_stock_threshold: Number(form.low_stock_threshold) || 5,
        imported_from_qoyod: !!form.imported_from_qoyod,
      };
      const { data } = await api.put("/settings", payload);
      setForm(data);
      toast.success("تم حفظ الإعدادات");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const testKey = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/qoyod/test", { api_key: form.qoyod_api_key });
      setTestResult({ ok: true, msg: `متصل ✓ — ${data.branches_count} فرع تم العثور عليها` });
      toast.success("المفتاح صحيح");
    } catch (e) {
      setTestResult({ ok: false, msg: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      await api.post("/qoyod/import", { api_key: form.qoyod_api_key });
      toast.success("بدأ الاستيراد");
      loadStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  const testSalla = async () => {
    setSallaTesting(true);
    try {
      const { data } = await api.post("/salla/test");
      toast.success(`متصل بمتجر ${data.store_name || data.store_id}`);
      loadSallaStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSallaTesting(false);
    }
  };

  const refreshSallaToken = async () => {
    setSallaRefreshing(true);
    try {
      const { data } = await api.post("/salla/refresh-token");
      const expiry = data.expires_at ? new Date(data.expires_at).toLocaleString("ar-SA-u-nu-latn", { hour12: false }) : "—";
      toast.success(`تم تجديد التوكن — صالح حتى ${expiry}`);
      loadSallaStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSallaRefreshing(false);
    }
  };

  const disconnectSalla = async () => {
    setSallaDisconnecting(true);
    try {
      await api.post("/salla/disconnect");
      toast.success("تم فصل ربط سلة");
      loadSallaStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSallaDisconnecting(false);
    }
  };

  const saveSallaCreds = async () => {
    try {
      await api.post("/salla/credentials", {
        client_id: form.salla_client_id || "",
        client_secret: form.salla_client_secret || "",
      });
      toast.success("تم حفظ بيانات تطبيق سلة");
      loadSallaStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyWebhook = () => {
    const url = sallaStatus?.webhook_url;
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success("تم نسخ رابط Webhook");
  };

  const running = !!status?.running;
  const progress = status?.total_pages ? Math.round((status.done_pages / status.total_pages) * 100) : 0;

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto min-h-screen" style={{ background: "var(--bg-page)" }}>
      <header className="mb-8 fade-up">
        <p className="text-[11px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-semibold">الإعدادات</p>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[var(--text-primary)] mt-1">تكوين المنصة</h1>
        <p className="text-[var(--text-secondary)] mt-2 text-sm">
          مفاتيح الربط مع قيود وسلة، واستيراد البيانات الحقيقية، وإعدادات المزامنة.
        </p>
      </header>

      <form onSubmit={save} className="space-y-8">

        {/* ── Integration Hub ── */}
        <section className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-3xl p-6 shadow-sm space-y-7 fade-up">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Plug className="w-4 h-4 text-[#004EEB]" />
                <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-bold">التكاملات</span>
              </div>
              <h2 className="text-lg font-black text-[var(--text-primary)] tracking-tight">مركز الربط</h2>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                ربط منصتك ببرامج المحاسبة ومنصات التجارة الإلكترونية.
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-1.5 text-[10px] text-[var(--text-muted)] bg-[var(--bg-card-hover)] border border-[var(--border-color)] px-2.5 py-1.5 rounded-xl">
              <Clock className="w-3 h-3" />
              قريباً المزيد
            </div>
          </div>

          {/* Accounting */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-3.5 h-3.5 text-[var(--text-muted)]" />
              <h3 className="text-[11px] tracking-[0.15em] uppercase font-bold text-[var(--text-muted)]">برامج المحاسبة</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {ACCOUNTING_PLATFORMS.map((p) => (
                <PlatformCard
                  key={p.id}
                  {...p}
                  connected={p.id === "qoyod" && !!form?.qoyod_api_key}
                />
              ))}
            </div>
          </div>

          {/* E-commerce */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ShoppingCart className="w-3.5 h-3.5 text-[var(--text-muted)]" />
              <h3 className="text-[11px] tracking-[0.15em] uppercase font-bold text-[var(--text-muted)]">منصات التجارة الإلكترونية</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {ECOMMERCE_PLATFORMS.map((p) => (
                <PlatformCard
                  key={p.id}
                  {...p}
                  connected={p.id === "salla" && !!sallaStatus?.connected}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Qoyod */}
        <section className="bg-[var(--bg-card)] border border-[var(--border-color)]/80 rounded-2xl p-6 space-y-5">
          <div className="flex items-center justify-between border-b border-[var(--border-color)] pb-3">
            <h2 className="text-base font-semibold text-[var(--text-primary)]">قيود — Qoyod</h2>
            {form.imported_from_qoyod && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm bg-[#00A86B]/10 text-[#00A86B]">
                <CheckCircle2 className="w-3.5 h-3.5" />
                بيانات حقيقية مُستوردة
              </span>
            )}
          </div>

          <Field label="Qoyod API Key" hint="مفتاح API من: قيود → الإعدادات العامة">
            <div className="flex gap-2">
              <input
                data-testid="settings-qoyod-key-input"
                type="text"
                className={input + " flex-1 mono"}
                value={form.qoyod_api_key}
                onChange={(e) => set("qoyod_api_key", e.target.value)}
                placeholder="c4151..."
              />
              <button
                type="button"
                onClick={testKey}
                disabled={testing || !form.qoyod_api_key}
                data-testid="qoyod-test-button"
                className="px-4 py-2.5 text-sm border border-[var(--border-color)] rounded-sm hover:border-[#0A0A0A] disabled:opacity-50 transition-colors"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : "اختبار"}
              </button>
            </div>
            {testResult && (
              <div className={`text-xs mt-2 ${testResult.ok ? "text-[#00A86B]" : "text-[#E63946]"}`}>
                {testResult.msg}
              </div>
            )}
          </Field>

          <div className="border border-dashed border-[var(--border-color)] rounded-sm p-4 bg-[var(--bg-card-hover)]/80">
            <div className="flex items-start gap-3 mb-3">
              <Database className="w-5 h-5 text-[#0044FF] mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold">
                  استيراد البيانات الحقيقية من قيود
                </div>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  يستبدل البيانات التجريبية ببيانات حسابك في قيود. العملية للقراءة فقط
                  ولا تُعدّل أي شيء داخل قيود.
                </p>
              </div>
            </div>

            {running && (
              <div className="mt-3" data-testid="import-progress">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-[var(--text-secondary)]">{status?.message}</span>
                  <span className="mono font-semibold text-[#0044FF]">{progress}%</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-[#0044FF] transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="flex gap-4 text-xs mt-2 mono">
                  <span>{status?.branches} فرع</span>
                  <span>{status?.products} منتج</span>
                  <span className="text-[var(--text-muted)]">صفحة {status?.done_pages}/{status?.total_pages}</span>
                </div>
              </div>
            )}

            {!running && status?.finished_at && (
              <div className="mt-3 text-xs text-[var(--text-secondary)] border-s-2 border-[#00A86B] ps-3 py-1">
                آخر استيراد: {new Date(status.finished_at).toLocaleString("ar-SA-u-nu-latn", { hour12: false })}
                {" — "}
                <span className="font-semibold">{status.message}</span>
              </div>
            )}

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  disabled={importing || running || !form.qoyod_api_key}
                  data-testid="qoyod-import-button"
                  className="mt-3 flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#0044FF] text-white px-4 py-2 text-sm rounded-sm font-medium transition-colors disabled:opacity-50"
                >
                  {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 rtl-flip" />}
                  <span>{running ? "جارٍ الاستيراد…" : "استيراد الآن من قيود"}</span>
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent dir="rtl">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-[#B45309]" />
                    تأكيد الاستيراد
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    سيتم <b>حذف</b> كل البيانات التجريبية الحالية واستبدالها ببيانات حسابك
                    الحقيقية من قيود (الفروع + جميع المنتجات). العملية <b>آمنة</b> — النظام يقرأ فقط
                    ولا يُعدّل أي شيء داخل قيود.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>إلغاء</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={runImport}
                    className="bg-[#0044FF] hover:bg-[#0033CC]"
                    data-testid="qoyod-import-confirm"
                  >
                    نعم، ابدأ الاستيراد
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </section>

        <section className="bg-[var(--bg-card)] border border-[var(--border-color)]/80 rounded-2xl p-6 space-y-5">
          <div className="flex items-center justify-between border-b border-[var(--border-color)] pb-3">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#004EEB]" />
              <h2 className="text-lg font-semibold">سلة — Salla</h2>
            </div>
            <div className="flex items-center gap-2">
              {sallaStatus?.connected ? (
                <>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm bg-[#00A86B]/10 text-[#00A86B]" data-testid="salla-status-connected">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    متصل
                    {sallaStatus?.store_id && <span className="mono ms-1">· {sallaStatus.store_id}</span>}
                  </span>
                  <button
                    type="button"
                    onClick={connectSalla}
                    disabled={sallaConnecting}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-sm border border-[var(--border-color)] text-[var(--text-muted)] hover:border-[#004EEB] hover:text-[#004EEB] disabled:opacity-50 transition"
                    data-testid="salla-reconnect-button"
                    title="إعادة ربط سلة"
                  >
                    {sallaConnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    {sallaConnecting ? "جارٍ الانتظار..." : "إعادة الربط"}
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm bg-red-50 text-red-500 border border-red-100" data-testid="salla-status-disconnected">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    غير متصل
                  </span>
                  <button
                    type="button"
                    onClick={connectSalla}
                    disabled={sallaConnecting}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-sm bg-[#004EEB] text-white hover:bg-[#0033CC] disabled:opacity-60 transition"
                    data-testid="salla-connect-button"
                  >
                    {sallaConnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    {sallaConnecting ? "جارٍ الانتظار..." : "ربط سلة"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Webhook URL — admin only */}
          {user?.role === "admin" && (
          <Field label="رابط استقبال التنبيهات (Webhook)" hint="انسخه في بورتال شركاء سلة ← التطبيق ← التنبيهات ← رابط استقبال التنبيهات">
            {(() => {
              const webhookUrl = sallaStatus?.webhook_url || "";
              return (
                <div className="flex gap-2 items-center">
                  <input
                    data-testid="salla-webhook-url"
                    readOnly
                    value={webhookUrl}
                    className={`${input} mono text-xs flex-1 bg-[var(--bg-card-hover)]`}
                  />
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("تم نسخ الرابط"); }}
                    className="px-3 py-2 text-xs border border-[var(--border-color)] rounded-sm hover:border-[#0A0A0A] flex items-center gap-1.5 shrink-0"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    نسخ
                  </button>
                </div>
              );
            })()}
          </Field>
          )}

          {/* Technical buttons — admin only */}
          {user?.role === "admin" && (
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={testSalla}
              disabled={sallaTesting || !sallaStatus?.connected}
              data-testid="salla-test-button"
              className="px-4 py-2 text-sm bg-[#0044FF] hover:bg-[#0033CC] disabled:opacity-40 text-white rounded-sm flex items-center gap-1.5"
            >
              {sallaTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              اختبار الاتصال
            </button>
            <button
              type="button"
              onClick={refreshSallaToken}
              disabled={sallaRefreshing || !sallaStatus?.connected}
              data-testid="salla-refresh-token-button"
              title="تجديد توكن سلة يدوياً باستخدام refresh_token المخزّن"
              className="px-4 py-2 text-sm bg-[var(--bg-card)] border border-slate-300 hover:bg-[var(--bg-card-hover)] disabled:opacity-40 text-[var(--text-secondary)] rounded-sm flex items-center gap-1.5"
            >
              {sallaRefreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              تجديد التوكن
            </button>
            {sallaStatus?.connected && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    disabled={sallaDisconnecting}
                    data-testid="salla-disconnect-button"
                    className="px-4 py-2 text-sm bg-[var(--bg-card)] border border-red-200 hover:bg-red-50 hover:border-red-400 disabled:opacity-40 text-red-500 rounded-sm flex items-center gap-1.5 transition-colors"
                  >
                    {sallaDisconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unplug className="w-3.5 h-3.5" />}
                    فصل الربط
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent dir="rtl">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      فصل ربط سلة
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      سيتم حذف التوكن المخزّن وقطع الاتصال بمتجر سلة. لن تتوقف المزامنة التلقائية تلقائياً، ستحتاج لإعادة الربط لاستئناف العمل.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>إلغاء</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={disconnectSalla}
                      className="bg-red-500 hover:bg-red-600 text-white"
                      data-testid="salla-disconnect-confirm"
                    >
                      نعم، فصل الربط
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          )} {/* end admin technical buttons */}

          {sallaStatus?.connected && user?.role === "admin" && (
            <div className="text-xs text-[var(--text-secondary)] border-s-2 border-[#00A86B] ps-3 py-1 mt-2 space-y-0.5">
              {sallaStatus.store_id && <div>معرّف المتجر: <span className="mono">{sallaStatus.store_id}</span></div>}
              {sallaStatus.scope && <div>الصلاحيات: <span className="mono">{sallaStatus.scope}</span></div>}
              {sallaStatus.expires_at && <div>انتهاء التوكن: <span className="mono">{new Date(sallaStatus.expires_at).toLocaleString("ar-SA-u-nu-latn", { hour12: false })}</span></div>}
            </div>
          )}
        </section>

        <section className="bg-[var(--bg-card)] border border-[var(--border-color)]/80 rounded-2xl p-6 space-y-5">
          <h2 className="text-base font-semibold text-[var(--text-primary)] border-b border-[var(--border-color)] pb-3">المزامنة التلقائية</h2>

          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium">تفعيل التزامن الدوري</div>
              <div className="text-xs text-[var(--text-muted)] mt-1">
                عند التفعيل، يقوم النظام بتزامن تلقائي كل فترة زمنية محددة.
              </div>
            </div>
            <Switch
              data-testid="settings-cron-switch"
              checked={form.cron_enabled}
              onCheckedChange={(v) => set("cron_enabled", v)}
            />
          </div>

          <Field label="الفاصل الزمني (بالدقائق)" hint="كل كم دقيقة يتم تشغيل التزامن التلقائي">
            <input
              data-testid="settings-interval-input"
              type="number"
              min={1}
              className={input}
              value={form.cron_interval_minutes}
              onChange={(e) => set("cron_interval_minutes", e.target.value)}
            />
          </Field>

          <Field label="الحد الأدنى للمخزون" hint="أقل كمية تعتبر منتجاً منخفض المخزون">
            <input
              data-testid="settings-threshold-input"
              type="number"
              min={0}
              className={input}
              value={form.low_stock_threshold}
              onChange={(e) => set("low_stock_threshold", e.target.value)}
            />
          </Field>
        </section>

        <div className="flex justify-end">
          <button
            data-testid="settings-save-button"
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 bg-[#0044FF] hover:bg-[#0033CC] disabled:opacity-60 text-white px-6 py-3 rounded-sm font-medium transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>حفظ الإعدادات</span>
          </button>
        </div>
      </form>

      {/* Help Topics — visible to all, managed by admin */}
      <div className="mt-8">
        <HelpTopicsSection isAdmin={user?.role === "admin"} />
      </div>

      {/* Pricing Plans — admin only */}
      {user?.role === "admin" && (
        <div className="mt-8">
          <PricingPlansSection />
        </div>
      )}

      {/* Testimonials — admin only */}
      {user?.role === "admin" && (
        <div className="mt-8">
          <TestimonialsSection />
        </div>
      )}

      {/* Blog Posts — admin only */}
      {user?.role === "admin" && (
        <div className="mt-8">
          <BlogSection />
        </div>
      )}
    </div>
  );
}
